import { supabase } from '../client';

export interface CalendarNote {
  id: string;
  org_id: string;
  user_id: string;
  note_date: string;  // 'YYYY-MM-DD'
  body: string;
  created_at: string;
  updated_at: string;
}

export const calendarNotesService = {
  /** Recent notes for sidebar / cross-month display (newest dates first). */
  async listRecent(orgId: string, userId: string, limit = 40): Promise<CalendarNote[]> {
    const cap = Math.min(Math.max(1, limit), 100);
    const { data, error } = await supabase
      .from('ggj_b2b_calendar_notes')
      .select('*')
      .eq('org_id', orgId)
      .eq('user_id', userId)
      .order('note_date', { ascending: false })
      .limit(cap);
    if (error) throw error;
    return (data ?? []) as CalendarNote[];
  },

  /** Fetch all notes for this user in a given month (YYYY-MM). */
  async listByMonth(orgId: string, userId: string, yearMonth: string): Promise<CalendarNote[]> {
    const start = `${yearMonth}-01`;
    const end   = `${yearMonth}-31`;
    const { data, error } = await supabase
      .from('ggj_b2b_calendar_notes')
      .select('*')
      .eq('org_id', orgId)
      .eq('user_id', userId)
      .gte('note_date', start)
      .lte('note_date', end)
      .order('note_date', { ascending: true });
    if (error) throw error;
    return (data ?? []) as CalendarNote[];
  },

  /** Upsert a note for one day. Deletes the row if body is empty. */
  async upsert(orgId: string, userId: string, noteDate: string, body: string): Promise<void> {
    if (!body.trim()) {
      await supabase
        .from('ggj_b2b_calendar_notes')
        .delete()
        .eq('org_id', orgId)
        .eq('user_id', userId)
        .eq('note_date', noteDate);
      return;
    }
    const { error } = await supabase
      .from('ggj_b2b_calendar_notes')
      .upsert(
        { org_id: orgId, user_id: userId, note_date: noteDate, body },
        { onConflict: 'org_id,user_id,note_date' }
      );
    if (error) throw error;
  },
};
